// The page's handle on the snapshot: the camera arms it, a drag on the slide
// (viewer/capture/CaptureLayer) chooses the rectangle, and the dialog opens on
// it. Armed, no annotation tool is (the drag is the snapshot's); Escape
// disarms; a slide swap or compare puts it away.

import { useCallback, useEffect, useRef, useState } from 'react';
import { slideTools, type ViewportEngine } from '../../viewer/ViewportEngine';
import type { CaptureRect } from '../../viewer/capture/region';

export interface UseCaptureOptions {
  engine: ViewportEngine | null;
  /** A single slide whose engine can hand out its tiles. */
  supported: boolean;
  /** Called as the camera arms: the page leaves whatever tool it had. */
  onArm?: () => void;
}

export interface UseCapture {
  supported: boolean;
  /** The next drag on the slide chooses the rectangle. */
  armed: boolean;
  /** The rectangle chosen, while the dialog is open. */
  request: CaptureRect | null;
  toggle: () => void;
  disarm: () => void;
  /** The layer chose a rectangle. */
  capture: (rect: CaptureRect) => void;
  /** The dialog closed. */
  close: () => void;
}

export function useCapture({ engine, supported: supportedIn, onArm }: UseCaptureOptions): UseCapture {
  const supported = supportedIn && !!engine && typeof engine.tileSource === 'function' && slideTools(engine.capabilities);
  const [armed, setArmed] = useState(false);
  const [request, setRequest] = useState<CaptureRect | null>(null);
  const onArmRef = useRef(onArm);
  onArmRef.current = onArm;
  // Read by toggle: a state updater must stay pure (React may run it again),
  // so the side effect of arming happens outside it.
  const armedRef = useRef(armed);
  armedRef.current = armed;

  const disarm = useCallback(() => setArmed(false), []);
  const close = useCallback(() => setRequest(null), []);
  const toggle = useCallback(() => {
    const arming = !armedRef.current;
    if (arming) onArmRef.current?.();
    setArmed(arming);
  }, []);
  const capture = useCallback((rect: CaptureRect) => {
    setArmed(false);
    setRequest(rect);
  }, []);

  // Another slide, compare, a document: nothing to snapshot here.
  useEffect(() => {
    if (supported) return;
    setArmed(false);
    setRequest(null);
  }, [supported]);
  useEffect(() => {
    setArmed(false);
    setRequest(null);
  }, [engine]);

  // Escape puts the camera down, as it leaves a tool.
  useEffect(() => {
    if (!armed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      setArmed(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [armed]);

  return { supported, armed, request, toggle, disarm, capture, close };
}
